<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="js">
import {
  onMounted,
  ref,
  watch,
  onUnmounted,
  inject,
  computed,
  toRef
} from 'vue';
import { storeToRefs } from 'pinia';
import * as d3 from 'd3';
import _ from 'lodash';
import { useModalStore } from '@/stores/modal.store';
import { useContextMenuStore } from '@/stores/contextMenu.store';

const autoStore = inject('autoStore');

const {
  instructionSteps,
  getOperationsRequest,
  customVariables,
  getAvailableVarsRequest
} = storeToRefs(autoStore);

const modalStore = useModalStore();
const ctxMenuStore = useContextMenuStore();

const instructionStepsObj = computed(() =>
  _.keyBy(instructionSteps.value, 'id')
);

function openInstructionModal(stepId) {
  modalStore
    .openModal('InstructionModal', {
      instructionStep: toRef(instructionStepsObj.value, stepId),
      availableVars: getAvailableVarsRequest.value.data,
      availableOperations: getOperationsRequest.value.data,
      customVariables: customVariables,
      createCustomVar: autoStore.createOrUpdateCustomVar
    })
    .then(({ event, params }) => {
      if (event !== 'save') return;

      autoStore.createOrUpdateInstructionStep(params, stepId);

      drawGraph();
      modalStore.closeModal();
    });
}

function openDeleteModal(stepId) {
  modalStore
    .openModal('ConfirmModal', {
      title: 'Вы действительно хотите удалить эту операцию?',
      btnConfirm: 'Удалить'
    })
    .then(({ event }) => {
      if (event !== MODAL_CONFIRM) return;

      autoStore.removeInstructionStep(stepId);

      drawGraph();
      modalStore.closeModal();
    })
    .catch(() => {});
}

function getMenuItems(node) {
  const menuItems = [];

  if (!node.children?.length) {
    menuItems.push({
      title: 'Создать дочерний шаг',
      action: () => autoStore.createEmptyStep(node.id)
    });
  }
  if (node.parent_id) {
    menuItems.push({
      title: 'Добавить предшествующий шаг',
      action: () => autoStore.createPrecedingStep(node.id)
    });
  }

  menuItems.push({
    title: 'Удалить',
    action: () => openDeleteModal(node.id)
  });

  return menuItems;
}

function operationName(operationId) {
  const operation = getOperationsRequest.value.data?.find(
    (op) => op.id == operationId
  );
  return operation?.title || '';
}

const svg = ref(null);
const menuContainer = ref(null);
const stratify = d3
  .stratify()
  .id((d) => d.id)
  .parentId((d) => d.parent_id);

const cluster = d3
  .tree()
  .nodeSize([400, 150])
  .separation(() => 1.1);

const drawGraph = () => {
  if (!instructionSteps.value || !instructionSteps.value.length) {
    return;
  }
  const stratifyData = stratify(instructionSteps.value);
  const rootNode = cluster(stratifyData);

  svg.value.selectAll('*').remove();

  const containerWidth = svg.value.node().getBoundingClientRect().width;
  const containerHeight = svg.value.node().getBoundingClientRect().height;

  const initialTransform = d3.zoomIdentity.translate(
    containerWidth / 2,
    containerHeight / 4
  );

  const root = svg.value.append('g').attr('transform', initialTransform);

  let nodeHeight = 60;
  const padding = 20; // Padding around the text

  const measureTextWidth = (text) => {
    const tempText = svg.value
      .append('text')
      .attr('font-size', 18)
      .attr('visibility', 'hidden')
      .text(text);
    const width = tempText.node().getBBox().width;
    tempText.remove();
    return width;
  };

  const drawNode = (obj) => {
    const nameWidth = measureTextWidth(obj.data.id);
    let nodeWidth = Math.max(nameWidth) + padding * 2;
    const opName = operationName(obj.data.operation_id || null);
    if (opName) {
      const operationNameWidth = opName ? measureTextWidth(opName) : 0;
      nodeWidth = Math.max(nameWidth, operationNameWidth) + padding * 2;
    }

    const x = obj.x - nodeWidth / 2;
    const y = obj.y - nodeHeight / 2;

    const g = root
      .append('g')
      .attr('transform', `translate(${x}, ${y})`)
      .style('cursor', 'pointer')
      .on('click', () => {
        openInstructionModal(obj.data.id);
        menuContainer.value?.remove();
        menuContainer.value = null;
      });
    g.append('rect')
      .attr('rx', 5)
      .attr('ry', 5)
      .attr('width', nodeWidth)
      .attr('height', nodeHeight)
      .attr('x', 0)
      .attr('y', 0)
      .attr('fill', '#FEFEFE')
      .attr('stroke', '#DDD')
      .attr('stroke-width', 1);

    g.append('text')
      .attr('x', padding)
      .attr('y', 25)
      .attr('font-size', 18)
      .text(obj.data.id);

    if (opName) {
      g.append('text')
        .attr('x', padding)
        .attr('y', 45)
        .attr('font-size', 14)
        .attr('fill', '#888')
        .text(opName);
    }

    g.append('text')
      .attr('x', 0)
      .attr('y', 25)
      .attr('width', 20)
      .attr('font-size', 19)
      .attr('cursor', 'pointer')
      .attr('fill', '#AEAEAE')
      .text('⋮')
      .on('click', (event) => {
        event.stopPropagation();

        ctxMenuStore.openContextMenu(event, getMenuItems(obj.data));
      });

    if (obj.children) {
      obj.children.forEach(drawNode);
    }
  };

  const drawLinks = () => {
    root
      .append('g')
      .selectAll('path')
      .data(rootNode.links())
      .enter()
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', (d) => {
        const true_id = d.source.data?.condition?.true_step_id;
        const false_id = d.source.data?.condition?.false_step_id;
        if (d.target.id == true_id) return 'green';
        if (d.target.id == false_id) return 'red';
        return '#555';
      })
      .attr('stroke-opacity', 0.4)
      .attr('stroke-width', 1.5)
      .attr('marker-end', 'url(#arrowhead)')
      .attr('marker-start', 'url(#dot)')
      .attr('d', (d) => {
        const path = d3.path();
        const xSource = d.source.x;
        const ySource = d.source.y + 30;
        const xTarget = d.target.x;
        const yTarget = d.target.y - 30;

        path.moveTo(xSource, ySource);
        path.lineTo(xSource, ySource + 25);
        if (xSource > xTarget) {
          path.arc(xSource - 20, ySource + 25, 20, 0, Math.PI / 2);
          path.lineTo(xTarget + 20, yTarget - 45);
          path.arc(xTarget + 20, yTarget - 25, 20, -1.5, Math.PI, true);
        } else if (xSource < xTarget) {
          path.arc(xSource + 20, ySource + 25, 20, Math.PI, Math.PI / 2, true);
          path.lineTo(xTarget - 20, yTarget - 45);
          path.arc(xTarget - 20, yTarget - 25, 20, Math.PI * 1.5, Math.PI * 2);
        }

        path.lineTo(xTarget, yTarget);
        return path.toString();
      });
  };

  drawNode(rootNode);
  drawLinks();
};
watch(instructionSteps, () => drawGraph(), { deep: true });
onMounted(() => {
  svg.value = d3.select('#steps');

  const zoom = d3.zoom().on('zoom', (event) => {
    svg.value.select('g').attr('transform', event.transform);
  });

  svg.value.call(zoom);

  const containerWidth = svg.value.node().getBoundingClientRect().width;
  const containerHeight = svg.value.node().getBoundingClientRect().height;
  const initialTransform = d3.zoomIdentity.translate(
    containerWidth / 2,
    containerHeight / 4
  );

  svg.value.call(zoom.transform, initialTransform);

  drawGraph();
});

onUnmounted(() => svg.value.on('.zoom', null));
</script>

<template>
  <div v-if="!instructionSteps.length" class="px-3 mt-3">
    <div class="orange-btn" @click="openInstructionModal()">
      Создать операцию
    </div>
  </div>
  <svg id="steps" class="chart">
    <defs>
      <marker
        id="arrowhead"
        viewBox="0 0 10 10"
        refX="10"
        refY="5"
        markerWidth="5"
        markerHeight="5"
        orient="auto"
      >
        <path d="M10 5 0 10 0 0Z" fill="#aaa"></path>
      </marker>
      <marker
        id="dot"
        viewBox="0 0 10 10"
        refX="5"
        refY="5"
        markerWidth="4"
        markerHeight="4"
        orient="auto"
      >
        <circle cx="5" cy="5" r="5" fill="#aaa"></circle>
      </marker>
    </defs>
  </svg>
</template>
